
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { Calendar, Clock, Users, Activity, AlertCircle, CheckCircle, ChevronRight } from 'lucide-react';

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone?: string;
  address?: string;
}

interface Visit {
  id: string;
  patient_id: string;
  nurse_id: string;
  scheduled_date: string;
  scheduled_start_time: string;
  scheduled_end_time: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  visit_type: string;
  patient?: Patient;
}

interface DashboardStats {
  todayVisits: number;
  completedVisits: number;
  inProgressVisits: number;
  scheduledVisits: number;
}

const NursingDashboard = () => {
  const { user, profile } = useAuth();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    todayVisits: 0,
    completedVisits: 0,
    inProgressVisits: 0,
    scheduledVisits: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user && profile) {
      fetchDashboardData();
    }
  }, [user, profile]);

  const fetchDashboardData = async () => {
    if (!user || !profile) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Build query - admin sees all visits, nurses see only their own
      let visitsQuery = supabase
        .from('visits')
        .select(`
          *,
          patient:patients(
            id,
            first_name,
            last_name,
            date_of_birth,
            phone,
            address
          )
        `)
        .eq('scheduled_date', today)
        .order('scheduled_start_time', { ascending: true });
        
      // Filter by nurse if not admin
      if (profile.role !== 'admin') {
        visitsQuery = visitsQuery.eq('nurse_id', user.id);
      }
      
      const { data: visitsData, error: visitsError } = await visitsQuery;
      
      if (visitsError) {
        console.error('Error fetching visits:', visitsError);
        throw new Error('Failed to load today\'s visits');
      }
      
      // Calculate stats from real data
      const todayVisits = visitsData?.length || 0;
      const completedVisits = visitsData?.filter(v => v.status === 'completed').length || 0;
      const inProgressVisits = visitsData?.filter(v => v.status === 'in_progress').length || 0;
      const scheduledVisits = visitsData?.filter(v => v.status === 'scheduled').length || 0;
      
      setVisits(visitsData || []);
      setStats({
        todayVisits,
        completedVisits,
        inProgressVisits,
        scheduledVisits
      });
      
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-4 max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 max-w-6xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
            <button 
              onClick={fetchDashboardData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Today's Overview</h2>
        <p className="text-gray-600">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </p>
      </div>
      
      {/* Stats Grid - Now with real data */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Total Visits</div>
                <div className="text-3xl font-bold text-blue-600">{stats.todayVisits}</div>
              </div>
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Completed</div>
                <div className="text-3xl font-bold text-green-600">{stats.completedVisits}</div>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">In Progress</div>
                <div className="text-3xl font-bold text-yellow-600">{stats.inProgressVisits}</div>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Scheduled</div>
                <div className="text-3xl font-bold text-gray-600">{stats.scheduledVisits}</div>
              </div>
              <Users className="h-8 w-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Schedule - Now with real data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Today's Schedule</span>
            {profile?.role === 'admin' && (
              <span className="text-sm font-normal text-gray-500">
                All nurses • {stats.todayVisits} visits
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {visits.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No visits scheduled for today</p>
                <p className="text-gray-400 text-sm mt-2">
                  {profile?.role === 'admin' 
                    ? 'Schedule visits for your team to get started'
                    : 'Contact your supervisor to schedule visits'
                  }
                </p>
              </div>
            ) : (
              visits.map(visit => (
                <div key={visit.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-gray-900">
                          {visit.patient?.first_name} {visit.patient?.last_name}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(visit.status)}`}>
                          {visit.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {visit.scheduled_start_time} - {visit.scheduled_end_time}
                          </span>
                          <span className="text-gray-500">•</span>
                          <span>{visit.visit_type}</span>
                        </div>
                        {visit.patient?.address && (
                          <div className="text-gray-500">
                            📍 {visit.patient.address}
                          </div>
                        )}
                        {visit.patient?.phone && (
                          <div className="text-gray-500">
                            📞 {visit.patient.phone}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NursingDashboard;
